import { buildServerDrivenPositionSizeColumnFields } from '@src/utils/columns/serverDrivenPositionSizeColumn';
import {
  getAgGridColId,
  isTalosUser,
  useDefaultColumns,
  type Column,
  type ColumnDef,
  type Position,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { useFeatureFlag, useUser } from '../../../../hooks';
import { useDisplaySettings } from '../../../../providers/DisplaySettingsProvider';
import type { PositionColumn } from '../types';

export const usePositionsSpotColumns = (defaultColumns: PositionColumn[]) => {
  const user = useUser();
  const { homeCurrency } = useDisplaySettings();
  const {
    showPositionsBlotterWarningColumn: showReconWarningFeatureFlag,
    enableServerDrivenNotionalAmount,
    enablePositionLegacyColumn,
  } = useFeatureFlag();

  const columnDefinitions = useMemo(() => {
    const primaryPosition = buildServerDrivenPositionSizeColumnFields<Position>({
      enableServerDrivenNotionalAmount,
      legacy: { field: 'Amount', currencyField: 'Asset' },
      server: {
        field: 'positionValue',
        currencyField: 'positionValueAsset',
        isValueAlreadyNotional: row => row.isPositionValueNotional,
      },
    });

    return new Map<string, Column>(
      compact([
        showReconWarningFeatureFlag
          ? {
              id: 'reconWarning',
              type: 'reconWarning',
              field: 'reconWarning',
              aggregate: true,
              sortable: true,
            }
          : undefined,
        {
          id: 'Asset',
          field: 'Asset',
          type: 'asset',
          sortable: true,
          title: 'Asset',
          params: {
            assetTypeField: 'AssetType',
            colorful: true,
          },
        },
        {
          id: 'MarketAccount',
          field: 'MarketAccount',
          type: 'marketAccount',
          sortable: true,
          initialSort: '-',
          params: {
            prefixCustomerMarketAccountsWithCpty: true,
          },
        },
        {
          id: 'Amount',
          field: primaryPosition.field,
          type: 'size',
          title: 'Position',
          sortable: true,
          aggregate: true,
          initialWidth: 150,
          params: {
            ...primaryPosition.params,
            highlightNegative: true,
            showCurrencyForNullPosition: false,
          },
          description: 'Exposure in quantity of assets.',
        },
        enablePositionLegacyColumn
          ? {
              id: 'position-legacy',
              field: primaryPosition.legacy.field,
              type: 'size',
              title: 'Position (Legacy)',
              sortable: true,
              aggregate: true,
              initialWidth: 150,
              initialHide: true,
              params: {
                ...primaryPosition.legacy.params,
                highlightNegative: true,
                showCurrencyForNullPosition: false,
              },
              description:
                'Exposure in quantity of assets. (legacy client-side calculation - for temporary comparison)',
            }
          : undefined,
        {
          field: 'AvgCost',
          type: 'price',
          initialWidth: 150,
          params: {
            quoteCurrencyField: 'AvgCostCurrency',
          },
        },
        {
          field: 'TotalPnL',
          type: 'size',
          title: 'Total LTD PnL',
          aggregate: true,
          params: {
            currencyField: 'PnLCurrency',
            highlightNegative: true,
          },
        },
        {
          field: 'AvgCostCurrency',
          type: 'currency',
          initialHide: true,
        },
        {
          field: 'RealizedPnL',
          type: 'size',
          title: 'Total Realized PnL',
          params: {
            currencyField: 'PnLCurrency',
            highlightNegative: true,
          },
          initialHide: true,
        },
        {
          field: 'UnrealizedPnL',
          type: 'size',
          title: 'Total Unrealized PnL',
          params: {
            currencyField: 'PnLCurrency',
            highlightNegative: true,
          },
          initialHide: true,
        },
        {
          field: 'Equivalent.UnrealizedPnL',
          type: 'size',
          title: `Total Unrealized PnL (${homeCurrency})`,
          aggregate: true,
          params: {
            currencyField: 'Equivalent.Currency',
            highlightNegative: true,
          },
          initialHide: true,
        },
        {
          field: 'PnLCurrency',
          type: 'currency',
          title: 'PnL Currency',
          initialHide: true,
        },
        {
          field: 'CumFees',
          type: 'size',
          title: 'Fees',
          params: {
            currencyField: 'FeeCurrency',
          },
          aggregate: true,
        },
        {
          field: 'Equivalent.CumFees',
          type: 'size',
          title: `Fees (${homeCurrency})`,
          params: {
            currencyField: 'Equivalent.Currency',
          },
          aggregate: true,
        },
        {
          field: 'UnsettledAmount',
          type: 'size',
          title: 'Unsettled Amount',
          params: {
            currencyField: 'Asset',
            highlightNegative: true,
          },
          aggregate: true,
        },
        {
          field: 'Equivalent.UnsettledAmount',
          type: 'size',
          title: `Unsettled Amount (${homeCurrency})`,
          params: {
            currencyField: 'Equivalent.Currency',
            highlightNegative: true,
          },
          aggregate: true,
        },
        {
          field: 'UnsettledAvgCost',
          type: 'price',
          initialWidth: 150,
          title: 'Unsettled Avg Cost',
          params: {
            quoteCurrencyField: 'AvgCostCurrency',
          },
        },
        {
          field: 'UnsettledPnL',
          type: 'size',
          title: 'Unsettled PnL',
          params: {
            currencyField: 'PnLCurrency',
            highlightNegative: true,
          },
          aggregate: true,
        },
        {
          field: 'Equivalent.UnsettledPnL',
          type: 'size',
          title: `Unsettled PnL (${homeCurrency})`,
          params: {
            currencyField: 'Equivalent.Currency',
            highlightNegative: true,
          },
          aggregate: true,
        },
        {
          field: 'LastUpdateTime',
          type: 'date',
          initialHide: true,
        },
        {
          field: 'Status',
          type: 'text',
          initialHide: true,
        },
        isTalosUser(user)
          ? {
              field: 'PositionSource',
              type: 'text',
              initialHide: true,
            }
          : undefined,
      ] satisfies (ColumnDef<Position> | undefined)[]).map(c => [getAgGridColId(c), c])
    );
  }, [homeCurrency, user, showReconWarningFeatureFlag, enableServerDrivenNotionalAmount, enablePositionLegacyColumn]);

  return useDefaultColumns(defaultColumns, columnDefinitions);
};
